// src/components/ImageEditModal.js

import React, { useState, useEffect } from "react";
import { FaTimes, FaUpload, FaTrash } from "react-icons/fa";
import { updateLocationImages } from "./locationsfunction"; // We will create this function

const LoadingSpinner = ({ size = "w-5 h-5" }) => (
  <div
    className={`animate-spin rounded-full border-2 border-white border-t-transparent ${size}`}
  />
);

const ImageEditModal = ({ isOpen, onClose, location, onSave }) => {
  const [images, setImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [urlsToDelete, setUrlsToDelete] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (location?.storeImages) {
      setImages(location.storeImages);
    }
    // Reset state when modal is closed or location changes
    return () => {
      setImages([]);
      setNewFiles([]);
      setUrlsToDelete([]);
      setError("");
      setIsSaving(false);
    };
  }, [isOpen, location]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).filter((file) =>
      file.type.startsWith("image/")
    );
    if (files.length === 0) return;

    const filesWithPreviews = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setNewFiles((prev) => [...prev, ...filesWithPreviews]);
  };

  const removeNewFile = (indexToRemove) => {
    const fileToRemove = newFiles[indexToRemove];
    URL.revokeObjectURL(fileToRemove.previewUrl);
    setNewFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const removeExistingImage = (urlToRemove) => {
    setUrlsToDelete((prev) => [...prev, urlToRemove]);
    setImages((prev) => prev.filter((img) => img.url !== urlToRemove));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError("");
    try {
      const updatedLocationData = await updateLocationImages(
        location.id,
        newFiles.map((f) => f.file), // Pass only the File objects
        urlsToDelete
      );
      onSave(updatedLocationData); // Update parent state
      onClose();
    } catch (err) {
      setError("Failed to save images. Please try again.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            Edit Images for {location.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-800 rounded-full"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {/* Image Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
            {/* Existing Images */}
            {images.map((image, index) => (
              <div key={image.url} className="relative group aspect-square">
                <img
                  src={image.url}
                  alt={`Store image ${index + 1}`}
                  className="w-full h-full object-cover rounded-md border"
                />
                <button
                  onClick={() => removeExistingImage(image.url)}
                  disabled={isSaving}
                  className={`absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full transition-opacity ${isSaving ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`}
                >
                  <FaTrash size={12} />
                </button>
              </div>
            ))}
            {/* New Preview Images */}
            {newFiles.map((image, index) => (
              <div key={index} className="relative group aspect-square">
                <img
                  src={image.previewUrl}
                  alt={`New image ${index + 1}`}
                  className="w-full h-full object-cover rounded-md border-2 border-blue-400"
                />
                <button
                  onClick={() => removeNewFile(index)}
                  disabled={isSaving}
                  className={`absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full transition-opacity ${isSaving ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`}
                >
                  <FaTrash size={12} />
                </button>
              </div>
            ))}

            {/* Upload Button */}
            <label className={`aspect-square flex flex-col items-center justify-center border-2 border-dashed rounded-md text-gray-500 ${isSaving ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
              <FaUpload size={24} />
              <span className="text-xs mt-2 text-center">Add Images</span>
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                disabled={isSaving}
                onChange={handleFileChange}
              />
            </label>
          </div>
        </div>

        <div className="flex justify-end items-center p-4 border-t mt-auto">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md mr-2 disabled:opacity-50"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-md flex items-center disabled:bg-blue-400"
            disabled={isSaving}
          >
            {isSaving ? <LoadingSpinner /> : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageEditModal;
